import React, { useState, useCallback, useLayoutEffect } from "react";
import { Box } from "@mui/material";
import { DynamicViewportOverlayRoot } from "./DynamicViewportOverlay.styles";
import { useSelectedOptions } from "../../context/SelectedOptionsContext";

// --- Custom Hook for Aspect Ratio Calculation ---
/**
 * Calculates the dimensions for the black bars based on viewport size and target aspect ratio.
 * It also handles the resize listener to update dimensions in real-time.
 * * @param {string} ratio - The target aspect ratio (e.g., "1:1", "16:9"). Defaults to '1:1'
 * to prevent the initial 'split' error if no ratio is provided.
 * @returns {{barWidth: number, barHeight: number}}
 */
const useAspectRatio = (ratio = "16:9") => {
  const [dimensions, setDimensions] = useState({
    barWidth: 0,
    barHeight: 0,
  });

  const calculateBars = useCallback(() => {
    // Get current viewport dimensions
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const parts = ratio.split(":");
    if (parts.length !== 2) {
      return;
    }

    const [ratioWidth, ratioHeight] = parts.map(Number);
    if (ratioHeight === 0 || ratioWidth === 0) {
      return;
    }

    const currentRatio = vw / vh;
    const targetRatio = ratioWidth / ratioHeight;

    let newBarWidth = 0;
    let newBarHeight = 0;

    if (currentRatio > targetRatio) {
      // Case 1: Viewport is WIDER than target (e.g., 16:9 on a widescreen monitor).
      // Result: Pillarbox (vertical bars). Content is limited by Vh.

      // Calculate max content width based on full height and target ratio
      const contentWidth = vh * targetRatio;

      // Bar width is the leftover horizontal space, divided by two
      newBarWidth = (vw - contentWidth) / 2;
      newBarHeight = 0;
    } else if (currentRatio < targetRatio) {
      // Case 2: Viewport is TALLER than target (e.g., 9:16 on a horizontal monitor).
      // Result: Letterbox (horizontal bars). Content is limited by Vw.

      // Calculate max content height based on full width and target ratio
      const contentHeight = vw / targetRatio;

      // Bar height is the leftover vertical space, divided by two
      newBarHeight = (vh - contentHeight) / 2;
      newBarWidth = 0;
    }
    // Case 3: Ratios match, bars stay 0.

    const finalBarWidth = Math.max(0, newBarWidth);
    const finalBarHeight = Math.max(0, newBarHeight);

    setDimensions({
      barWidth: finalBarWidth,
      barHeight: finalBarHeight,
    });
  }, [ratio]);

  // Use useLayoutEffect for synchronous recalculation on initial load and resize
  useLayoutEffect(() => {
    calculateBars();

    const handleResize = () => {
      calculateBars();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [calculateBars]);

  return dimensions;
};

// --- DynamicViewportOverlay Component (The final reusable component) ---

/**
 * Renders a dynamic overlay with black, blurred bars to enforce a visual aspect ratio crop
 * based on the provided `aspectRatio` prop. This component acts purely as a mask.
 *
 * @param {string} aspectRatio - The target aspect ratio (e.g., "1:1", "16:9"). Defaults to '1:1'.
 */
export const DynamicViewportOverlay = ({ open }) => {
  const { currentAspectRatio } = useSelectedOptions();
  const { barWidth, barHeight } = useAspectRatio(currentAspectRatio);

  // Style for the common black bars (now blurred)
  const barStyle = {
    position: "absolute",
    backgroundColor: "rgba(0,0,0,0.9)",
    // ZIndex for the bars must be higher than the content they sit over, but lower than any control panel/modals.
    zIndex: 9,
    pointerEvents: "none", // Allows user interaction with elements below the bars
    transition: "all 0.3s ease-in-out", // Smooth transition for resizing
    backdropFilter: "blur(2px)",
    WebkitBackdropFilter: "blur(2px)",
  };

  return (
    <DynamicViewportOverlayRoot open={open}>
      {/* 1. Top Bar */}
      <Box
        sx={{
          ...barStyle,
          top: 0,
          left: 0,
          width: "100%",
          height: barHeight,
        }}
      />
      {/* 2. Bottom Bar */}
      <Box
        sx={{
          ...barStyle,
          bottom: 0,
          left: 0,
          width: "100%",
          height: barHeight,
        }}
      />
      {/* 3. Left Bar */}
      <Box
        sx={{
          ...barStyle,
          // Start vertical bars below the horizontal bars (if they exist)
          top: barHeight,
          left: 0,
          // Calculate height: 100vh minus the combined height of the top and bottom bars
          height: `calc(100% - ${barHeight * 2}px)`,
          width: barWidth,
        }}
      />
      <Box
        sx={{
          ...barStyle,
          // Start vertical bars below the horizontal bars (if they exist)
          top: barHeight,
          right: 0,
          // Calculate height: 100% minus the combined height of the top and bottom bars
          height: `calc(100% - ${barHeight * 2}px)`,
          width: barWidth,
        }}
      />
    </DynamicViewportOverlayRoot>
  );
};
